import React, { Fragment, useCallback, useState } from 'react';
import EngineScoringContainer from './EngineScoringContainer';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import Portal from 'utils/portal/Portal';
import { areas } from 'constants/areas';
import { getInt } from '../../../../utils/functions';
import Header from '../edit/header/Header';
import HeaderLinks from '../edit/header/HeaderLinks';
import { withAreaGetter } from 'providers/AreasConsumers';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import { DUPLICATE_ENGINE_SCORING, ENGINE_SCORING_CREATE_NEW_VERSION } from './mutations';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { useMutation, useSubscription } from 'hooks/apollo';
import { ENGINE_SCORING_DUPLICATED, ENGINE_SCORING_NEW_VERSION_CREATED } from './subscriptions';
import PermissionCheck from 'components/common/PermissionCheck';
import { ADMIN_SCORING } from 'constants/rightsCodes';

const Route = props => {
  const historyContext = useHistoryWithContext();
  const engineScoringId = props.match.params.engineScoringId && getInt(props.match.params.engineScoringId);
  const entityIdentifier = props.match.params.entityIdentifierForTest;

  const [t] = useTranslation();
  const addToast = useToast();

  const [title, setTitle] = useState('');
  const [canTestScoring, setCanTestScoring] = useState(true);
  const [entityIdentifierForTest, setEntityIdentifierForTest] = useState('');
  const { getElemByAreaKey } = props;

  const [duplicateEngineScoringGrid] = useMutation(DUPLICATE_ENGINE_SCORING);

  const [newVersionEngineScoringGrid] = useMutation(ENGINE_SCORING_CREATE_NEW_VERSION);

  useSubscription(ENGINE_SCORING_DUPLICATED, {
    onData: ({ data: { data } }) => {
      addToast(t('EngineScoringGrid.EngineScoringDuplicated'), 'success');
      if (data.engineScoringDuplicated.engineScoringId) onRedirect(data.engineScoringDuplicated.engineScoringId);
    }
  });

  useSubscription(ENGINE_SCORING_NEW_VERSION_CREATED, {
    onData: ({ data: { data } }) => {
      addToast(t('EngineScoringGrid.EngineScoringNewVersionCreated'), 'success');
      if (data.engineScoringNewVersionCreated.engineScoringId) onRedirect(data.engineScoringNewVersionCreated.engineScoringId);
    }
  });

  const onEntityIdentifierForTestChanged = useCallback(entityIdentifierForTest => {
    setEntityIdentifierForTest(entityIdentifierForTest);
  }, []);

  const onChangeTitle = useCallback(title => {
    setTitle(title);
  }, []);

  const onClickClose = useCallback(() => {
    historyContext.push(`/scoringEngine`);
  }, [historyContext]);

  const onRedirect = useCallback(
    engineScoringId => {
      historyContext.push(`/scoringEngine/${engineScoringId}`);
    },
    [historyContext]
  );

  const onDuplicate = useCallback(() => {
    duplicateEngineScoringGrid({ variables: { input: { engineScoringId } } });
  }, [duplicateEngineScoringGrid, engineScoringId]);

  const onScoringTest = () => {
    if (canTestScoring) window.open(window.location.href + `/test`, '_blank');
    localStorage.setItem('entityIdentifierForTest', entityIdentifierForTest);
  };

  const onNewVersion = useCallback(() => {
    newVersionEngineScoringGrid({ variables: { input: { engineScoringId } } });
  }, [engineScoringId, newVersionEngineScoringGrid]);

  return (
    <Fragment>
      <Portal container={getElemByAreaKey(areas.header)}>
        <Header title={title} />
      </Portal>
      <Portal container={getElemByAreaKey(areas.headerLinks)}>
        <PermissionCheck authorizationCode={ADMIN_SCORING} withForbiddenPage={false}>
          <HeaderLinks
            canTestScoring={canTestScoring}
            onClickClose={onClickClose}
            onDuplicate={onDuplicate}
            onScoringTest={onScoringTest}
            onNewVersion={onNewVersion}
            id={engineScoringId}
            disabled={false}
          />
        </PermissionCheck>
      </Portal>
      <PermissionCheck authorizationCode={ADMIN_SCORING}>
        <EngineScoringContainer
          setCanTestScoring={setCanTestScoring}
          engineScoringId={engineScoringId}
          onRedirect={onRedirect}
          onChangeTitle={onChangeTitle}
          openScoringTest={false}
          onEntityIdentifierForTestChanged={onEntityIdentifierForTestChanged}
          entityIdentifierForTest={entityIdentifier}
        />
      </PermissionCheck>
    </Fragment>
  );
};

Route.propTypes = {
  match: PropTypes.object.isRequired,
  getElemByAreaKey: PropTypes.func.isRequired
};

export default withAreaGetter(withRouter(Route));
